import { z } from 'zod';

export const insightStatuses = ['draft', 'published', 'dismissed'] as const;
export type InsightStatus = (typeof insightStatuses)[number];
export const insightStatusSchema = z.enum(insightStatuses);

// Insight lifecycle is a SEPARATE axis from status: closeInsight sets only closed_at and never
// touches status, so a closed insight keeps status 'draft'. This drives the read filter, not a
// persisted column -- there is nothing to migrate. Named as a FILTER vocabulary, not a domain one:
// 'all' is not a state an insight can be in, only a query option, so this must never be typed as
// the shape of an individual insight's own lifecycle state.
export const insightLifecycleFilters = ['open', 'closed', 'all'] as const;
export type InsightLifecycleFilter = (typeof insightLifecycleFilters)[number];

export const insightValences = ['risk', 'opportunity'] as const;
export type InsightValence = (typeof insightValences)[number];
export const insightValenceSchema = z.enum(insightValences);

export const insightKinds = ['report', 'action'] as const;
export type InsightKind = (typeof insightKinds)[number];
export const insightKindSchema = z.enum(insightKinds);

export const recommendationPriorities = ['low', 'medium', 'high', 'urgent'] as const;
export type RecommendationPriority = (typeof recommendationPriorities)[number];
export const recommendationPrioritySchema = z.enum(recommendationPriorities);

export const recommendationStatuses = ['pending', 'accepted', 'dismissed', 'completed'] as const;
export type RecommendationStatus = (typeof recommendationStatuses)[number];
export const recommendationStatusSchema = z.enum(recommendationStatuses);

export const recommendationRatings = ['up', 'down'] as const;
export type RecommendationRating = (typeof recommendationRatings)[number];
export const recommendationRatingSchema = z.enum(recommendationRatings);

// Preset reasons a user can give when rating a recommendation down (see the "Why is this not
// relevant?" picker). 'Something else' is not a member here — it is captured as free-text `comment`.
export const recommendationFeedbackReasons = [
  'not_relevant_portfolio',
  'already_handled',
  'value_estimate_off',
  'wrong_timing',
  'disagree',
] as const;
export type RecommendationFeedbackReason = (typeof recommendationFeedbackReasons)[number];
export const recommendationFeedbackReasonSchema = z.enum(recommendationFeedbackReasons);

export const timeHorizons = ['sprint', 'epic', 'quarter', 'halfyear'] as const;
export type TimeHorizon = (typeof timeHorizons)[number];
export const timeHorizonSchema = z.enum(timeHorizons);

// Canon §10 decision dimensions — the kind of value an action addresses. Shared rather than
// SIR-internal because the recommendation surface groups them into its four product lenses;
// the per-action map that produces them stays in apps/sir's action catalog.
export const dimensions = [
  'risk',
  'revenue',
  'cost',
  'opportunity_cost',
  'retention',
  'experience',
  'operational_efficiency',
] as const;
export type Dimension = (typeof dimensions)[number];
export const dimensionSchema = z.enum(dimensions);

// The action catalog's ids. The catalog itself — each action's label, guidance, time horizon and
// dimensions — stays in apps/sir (`computation-pass/action-catalog.ts`, which re-exports this list);
// only the id vocabulary lives here, for the same reason `dimensions` does: the surface needs it, and
// a hand-kept second copy in apps/frontend drifts silently.
//
// Order is meaningful and grouped by the horizon the action belongs to (sprint / epic / quarter /
// half-year), because `catalogPromptBlock` serialises this list into the recommender prompt.
export const catalogActionIds = [
  // sprint — EM, execution
  'unblock',
  'reassign',
  'split',
  'clarify',
  'cut_scope',
  'notify',
  // epic — EM/PMO, structural
  'recalibrate_commitment',
  'set_wip_or_scope_cap',
  'redistribute_ownership',
  'add_reviewer_rotation',
  'pair_or_knowledge_share',
  'add_review_cycle',
  'break_up_epic',
  'raise_in_retro',
  // quarter — PMO/SLT, portfolio
  'reprioritize',
  'descope_or_defer_initiative',
  'reallocate_capacity',
  'invest_in_skill',
  'request_budget_or_authority',
  'absorb_or_defer_demand',
  'expedite_for_revenue_account',
  // half-year / FY — SLT/C-suite, decision-surfacing
  'surface_strategic_decision',
  'reset_kr_or_expectations',
  'pursue_opportunity_or_counter_threat',
] as const;
export type CatalogActionId = (typeof catalogActionIds)[number];

const CATALOG_ACTION_ID_SET: ReadonlySet<string> = new Set(catalogActionIds);

// A persisted `catalogAction` is a plain string: rows predating the catalog carry null, and a row
// written by a newer deploy can carry an id this build does not know. Both sides narrow through
// here rather than casting.
export const isCatalogActionId = (value: string): value is CatalogActionId =>
  CATALOG_ACTION_ID_SET.has(value);

export const signalSeverities = ['low', 'medium', 'high', 'critical'] as const;
export type SignalSeverity = (typeof signalSeverities)[number];
export const signalSeveritySchema = z.enum(signalSeverities);

// The level a signal was computed at. scope_key is '' at org scope and the sub-scope id otherwise,
// so without the kind a key like "42" is ambiguous between loop 42 and team 42.
export const scopeKinds = ['org', 'loop', 'team'] as const;
export type ScopeKind = (typeof scopeKinds)[number];
export const scopeKindSchema = z.enum(scopeKinds);
